import { useTranslation } from "react-i18next";
import { useMerchantLocations, useUpdateActiveLocationsMutation } from "@/stores/queries/merchant.query";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { useActiveLocationsStore } from "@/stores/useActiveLocationsStore";
import { useState, useEffect } from "react";
import { MultiSelectPanel, type MultiSelectItem } from "@/components/ui/MultiSelectPanel";
import { Skeleton } from "@/components/ui/skeleton";
import { useLayoutStore } from "@/stores/useLayoutStore";
import { TooltipText } from "@/components/core/TooltipText";

interface MerchantLocationProps {
  readonly popoverClassName?: string;
}

export function MerchantLocation({ popoverClassName = "w-[85vw] p-2 ml-6" }: MerchantLocationProps) {
  const { t } = useTranslation();
  const { mutate: updateLocations, isPending: isUpdating } = useUpdateActiveLocationsMutation();
  const { activeLocationIds, availableLocations } = useActiveLocationsStore();
  const { data: location, isLoading } = useMerchantLocations();
  const merchantName = location?.data?.merchantName || t("common.merchantAppName");
  const disableLocationSelection = useLayoutStore((state) => state.disableLocationSelection);
  
  const [pendingSelection, setPendingSelection] = useState<string[]>(activeLocationIds);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPendingSelection(activeLocationIds);
    }
  }, [isOpen, activeLocationIds]);

  if (availableLocations.length === 0) {
    return null;
  }

  const isSingleLocation = availableLocations.length === 1;
  const isPendingAllSelected = pendingSelection.length === availableLocations.length;

  const items: MultiSelectItem[] = availableLocations.map(loc => ({
    id: loc.locationId,
    label: loc.name,
  }));

  const handleToggle = (id: string) => {
    let newSelection: string[];
    
    if (isPendingAllSelected) {
      newSelection = availableLocations
        .filter(loc => loc.locationId !== id)
        .map(loc => loc.locationId);
    } else {
      if (pendingSelection.includes(id)) {
        newSelection = pendingSelection.filter(itemId => itemId !== id);
      } else {
        newSelection = [...pendingSelection, id];
      }
    }
    
    setPendingSelection(newSelection);
  };

  const handleSelectAll = () => {
    if (isPendingAllSelected) {
      setPendingSelection([]);
    } else {
      const allLocationIds = availableLocations.map(loc => loc.locationId);
      setPendingSelection(allLocationIds);
    }
  };

  const handleSave = () => {
    if (pendingSelection.length === 0) {
      return;
    }

    updateLocations(pendingSelection);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setPendingSelection(activeLocationIds);
    setIsOpen(false);
  };

  if(isLoading) {
    return <Skeleton className="h-6 w-32" />;
  }


  if (isSingleLocation) {
    const locationName = location?.data?.locations[0]?.name || '';
    
    return (
      <TooltipText    
        className="font-bold text-dark-foreground px-2"
      >
        {locationName}
      </TooltipText>
    );
  }

  if (disableLocationSelection) {
    return (
      <TooltipText
        text={merchantName}
        className="font-bold text-sm text-dark-foreground px-2"
      />
    );
  }

  // For multiple locations, show dropdown
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="font-bold text-dark-foreground truncate max-w-[250px] px-2 hover:bg-accent/50"
          disabled={isUpdating}
          type="button"
        >
          <span className="truncate">{merchantName}</span>
          <ChevronDown className="ml-1 h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className={popoverClassName} align="center">
        <MultiSelectPanel
          items={items}
          selectedIds={pendingSelection}
          onToggle={handleToggle}
          onSelectAll={handleSelectAll}
          onSave={handleSave}
          onCancel={handleCancel}
          isAllSelected={isPendingAllSelected}
          isSaveDisabled={pendingSelection.length === 0 || isUpdating}
          selectAllLabel={t("locations.allLocations")}
          saveLabel={t("common.save")}
          cancelLabel={t("common.cancel")}
        />
      </PopoverContent>
    </Popover>
  );
}
